// Admin console: Customer detail — the ONE resolved effective-limits view
// (value + source per field, never raw plan/features/override JSON), usage
// vs. plan limits, owned buckets, and plan assignment. See
// docs/architecture/admin-console-concept.md §3.2/§4.2.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import type { EffectiveLimitField } from '../api/types';
import { useAsync, usePaginated } from '../lib/hooks';
import { portalUrl } from '../lib/appUrls';
import { useLang } from '../lib/useLang';
import { formatBytes, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Badge, Button, Card, CardHead, Stat } from '../components/ui';
import { useToast } from '../components/Toast';
import { OverrideEditor } from './AdminOverrideEditor';
import { AdminBucketTools, RenameTenantDialog } from './AdminBucketTools';
import { IconEye } from '../components/Icons';

const PAGE = 20;

// Canonical field order mirrors the Capsyra Policies CSV / plan-version
// schema (subscriptions.go:canonicalLimitFields), so the resolved view reads
// like the source sheet rather than an arbitrary map order.
const FIELD_ORDER = [
  'writeSlaHours',
  'minStoragePerArchiveBytes',
  'rateLimitWriteBytesPerDay',
  'rateLimitReadBytesPerDay',
  'verificationLevel',
  'verificationFrequency',
  'retrievalOptions',
  'priceWriteEurPerGb',
  'priceReadFreeLifetimeCount',
  'priceReadEurPerReadout',
  'priceStorageEurPerGbYear',
  'physicalPackaging',
  'ttfbReadingHours',
  'ttfbSiteId',
  'eccProfile',
  'storageLocationSiteId',
  'minBillableObjectBytes',
  'minBillableReadBytes',
  'mediumRedundancyPercent',
  'writeTriggers',
];

function sourceTone(source: EffectiveLimitField['source']): 'accent' | 'info' | 'default' {
  if (source === 'override') return 'accent';
  if (source === 'features') return 'info';
  return 'default';
}

function displayValue(v: unknown): string {
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? '✓' : '—';
  return String(v ?? '—');
}

export function AdminCustomerDetail() {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const { id = '' } = useParams();
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const tenants = useAsync(() => adminApi.adminTenants(), []);
  const limits = useAsync(() => adminApi.adminTenantEffectiveLimits(id), [id]);
  const usage = useAsync(() => adminApi.adminTenantUsage(id), [id]);
  const archives = usePaginated((cursor) => adminApi.adminTenantArchives(id, PAGE, cursor), [id]);

  const tenant = tenants.data?.items.find((c) => c.id === id);

  const viewAsTenant = async () => {
    try {
      await adminApi.adminImpersonate(id);
      // Full navigation to the CUSTOMER portal — a different host in the split
      // deployment, and the session cookie now scopes as this tenant, so the
      // app must reload rather than client-side route.
      window.location.href = portalUrl('/');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <>
      <PageHeader
        title={tenant?.name ?? id}
        subtitle={t('admin.customerDetail.subtitle')}
        actions={
          <>
            <Button variant="secondary" onClick={viewAsTenant} title={t('admin.customerDetail.viewAsHint')}>
              <IconEye width={18} height={18} />
              {t('admin.customerDetail.viewAs')}
            </Button>
            <Button variant="secondary" onClick={() => setRenaming(true)}>
              {t('admin.customerDetail.rename')}
            </Button>
            <Button variant="primary" onClick={() => setEditing(true)}>
              {t('admin.customerDetail.editPlan')}
            </Button>
          </>
        }
      />

      <div className="stack">
        <Card>
          <CardHead title={t('admin.customerDetail.effectiveLimits')} />
          <ApiState status={limits.status} error={limits.error} data={limits.status === 'error' ? null : limits.data} onRetry={limits.reload}>
            {(d) => {
              const keys = [...FIELD_ORDER.filter((k) => k in d.fields), ...Object.keys(d.fields).filter((k) => !FIELD_ORDER.includes(k))];
              return (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('admin.customerDetail.colField')}</th>
                        <th>{t('admin.customerDetail.colValue')}</th>
                        <th>{t('admin.customerDetail.colSource')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keys.map((k) => (
                        <tr key={k}>
                          <td className="cell-strong">{t(`admin.fields.${k}`, k)}</td>
                          <td>{displayValue(d.fields[k].value)}</td>
                          <td>
                            <Badge tone={sourceTone(d.fields[k].source)}>{t(`admin.source.${d.fields[k].source}`)}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }}
          </ApiState>
        </Card>

        <Card>
          <CardHead title={t('admin.customerDetail.usage')} />
          <ApiState status={usage.status} error={usage.error} data={usage.status === 'error' ? null : usage.data} onRetry={usage.reload}>
            {(u) => (
              <div className="grid grid--stats">
                <Stat label={t('admin.customerDetail.bytesWritten')} value={formatBytes(u.bytesWritten, lang)} />
                <Stat
                  label={t('admin.fields.rateLimitWriteBytesPerDay')}
                  value={u.rateLimitWriteBytesPerDay ? formatBytes(Number(u.rateLimitWriteBytesPerDay.value), lang) + '/d' : '—'}
                />
                <Stat
                  label={t('admin.fields.rateLimitReadBytesPerDay')}
                  value={u.rateLimitReadBytesPerDay ? formatBytes(Number(u.rateLimitReadBytesPerDay.value), lang) + '/d' : '—'}
                />
              </div>
            )}
          </ApiState>
        </Card>

        <Card>
          <CardHead title={t('admin.customerDetail.buckets')}>
            <AdminBucketTools tenantId={id} onChanged={() => archives.reload()} />
          </CardHead>
          <ApiState
            status={archives.status}
            error={archives.error}
            data={archives.status === 'error' ? null : archives.items}
            onRetry={archives.reload}
          >
            {(items) =>
              items.length === 0 ? (
                <EmptyState title={t('admin.customerDetail.bucketsEmpty')} />
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>{t('common.name')}</th>
                          <th className="num">{t('archives.colObjects')}</th>
                          <th className="num">{t('archives.colSize')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((a) => (
                          <tr key={a.id}>
                            <td className="cell-strong">{a.name}</td>
                            <td className="num">{formatNumber(a.objects, lang)}</td>
                            <td className="num">{formatBytes(a.bytes, lang)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <LoadMore hasMore={archives.hasMore} loading={archives.loadingMore} onLoadMore={archives.loadMore} />
                </>
              )
            }
          </ApiState>
        </Card>
      </div>

      {editing ? (
        <OverrideEditor
          tenantId={id}
          tenantName={tenant?.name ?? id}
          limits={limits.data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            toast.success(t('admin.customerDetail.assigned'));
            limits.reload();
            usage.reload();
          }}
        />
      ) : null}

      {renaming ? (
        <RenameTenantDialog
          tenantId={id}
          current={tenant?.name ?? id}
          onClose={() => setRenaming(false)}
          onSaved={() => {
            setRenaming(false);
            toast.success(t('admin.customerDetail.renamed'));
            tenants.reload();
          }}
        />
      ) : null}
    </>
  );
}
